import { useEffect, useRef, useState } from 'react';

function useVisible() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { visible, ref };
}

export default function MissionVisionSection() {
  const mission = useVisible();
  const vision = useVisible();

  return (
    <section className="py-24 md:py-32 px-6 md:px-16 lg:px-24 bg-white">
      <div className="max-w-6xl mx-auto space-y-24 md:space-y-32">
        {/* Misión */}
        <div ref={mission.ref} className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <div className={`w-full lg:w-1/2 transition-all duration-1000 ${mission.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
              <img
                src="https://readdy.ai/api/search-image?query=artisan%20perfumer%20hands%20carefully%20selecting%20dried%20flowers%20and%20natural%20botanical%20ingredients%20warm%20earth%20tones%20elegant%20workspace%20craftmanship%20detail%20close%20up&width=700&height=500&seq=about-mission&orientation=landscape"
                alt="Artesanía en fragancias"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className={`w-full lg:w-1/2 transition-all duration-1000 delay-200 ${mission.visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <p className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-4">Misión</p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Impulsar la Excelencia de la Perfumería
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Distribuir insumos de perfumería fina que permitan a nuestros clientes
              cautivar los sentidos y elevar sus productos. Nos comprometemos con la
              excelencia, la sostenibilidad y la innovación permanente para
              ofrecer soluciones que marquen la diferencia en el mercado latinoamericano.
            </p>
          </div>
        </div>

        {/* Visión */}
        <div ref={vision.ref} className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20">
          <div className={`w-full lg:w-1/2 transition-all duration-1000 ${vision.visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-lg">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20luxury%20fragrance%20boutique%20interior%20elegant%20glass%20display%20shelves%20warm%20lighting%20minimalist%20design%20high%20end%20perfume%20showroom%20atmosphere&width=700&height=500&seq=about-vision&orientation=landscape"
                alt="Showroom de fragancias"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className={`w-full lg:w-1/2 transition-all duration-1000 delay-200 ${vision.visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <p className="text-xs uppercase tracking-[0.25em] text-gray-400 mb-4">Visión</p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Ser Referentes en Distribución de Insumos
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Ser reconocidos como la empresa líder en distribución de insumos para
              perfumería fina en América Latina, respaldando emprendedores y empresas
              con las materias primas más puras y selectas del mercado. Buscamos expandir
              nuestra red llevando calidad y abastecimiento estratégico a los
              mercados más exigentes de la región.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}